// The reporting period of the report tables: two month-year pickers (From, To) and a Last 12 Months reset. Picking only changes a draft, so stepping
// through months and years sends nothing; Apply hands over one complete valid range. Future months are disabled; a pick that passes the other end
// moves that end with it (reportMonths.withFrom / withTo), so an invalid pair cannot exist.
import { useEffect, useId, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { translations } from "../../utils/lang";
import { MONTH_NAMES, REPORT_MIN_YEAR, currentReportMonth, formatMonth, isFutureMonth, monthKey, rollingWindow, withFrom, withTo } from "../../utils/reportMonths";
import { Button } from "../ui";
import { getReportText } from "./reportText";
import "./MonthRangeFilter.css";

const MonthPicker = ({ label, value, onPick, lang, now }) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => Number(value.slice(0, 4)));
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();
  const labels = translations[lang].filter.month;
  const text = getReportText(lang).period;
  const currentYear = Number(currentReportMonth(now).slice(0, 4));

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  const toggle = () => {
    setYear(Number(value.slice(0, 4)));
    setOpen((current) => !current);
  };
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div
      className="month-picker"
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          close();
        }
      }}
    >
      <span className="month-picker__label">{label}</span>
      <button ref={triggerRef} type="button" className="month-picker__trigger" aria-label={`${label}: ${formatMonth(value, labels)}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={toggle}>
        {formatMonth(value, labels)}
      </button>
      {open && (
        <div id={panelId} className="month-picker__panel" role="dialog" aria-label={label}>
          <div className="month-picker__years">
            <button type="button" aria-label={text.previousYear} disabled={year <= REPORT_MIN_YEAR} onClick={() => setYear(year - 1)}>
              {lang === "ar" ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
            <strong aria-live="polite">{year}</strong>
            <button type="button" aria-label={text.nextYear} disabled={year >= currentYear} onClick={() => setYear(year + 1)}>
              {lang === "ar" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </div>
          <div className="month-picker__months">
            {MONTH_NAMES.map((name, index) => {
              const key = monthKey(year, index + 1);
              return (
                <button
                  key={name}
                  type="button"
                  className={key === value ? "selected" : ""}
                  aria-pressed={key === value}
                  disabled={isFutureMonth(key, now)}
                  onClick={() => {
                    onPick(key);
                    close();
                  }}
                >
                  {labels[name] ?? name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function MonthRangeFilter({ lang, range, onApply, onReset, loading = false, now = new Date() }) {
  const text = getReportText(lang).period;
  const labels = translations[lang].filter.month;
  const [draft, setDraft] = useState({ from: range.from, to: range.to });
  useEffect(() => setDraft({ from: range.from, to: range.to }), [range.from, range.to]);

  const changed = draft.from !== range.from || draft.to !== range.to;
  const rolling = rollingWindow(now);
  const onRolling = range.isDefault && !changed && range.from === rolling.from;

  return (
    <section className="month-range" aria-label={text.label}>
      <div className="month-range__fields">
        <MonthPicker label={text.from} value={draft.from} lang={lang} now={now} onPick={(key) => setDraft(withFrom(draft, key))} />
        <MonthPicker label={text.to} value={draft.to} lang={lang} now={now} onPick={(key) => setDraft(withTo(draft, key))} />
        <Button size="sm" variant="primary" appearance="filled" disabled={!changed || loading} onClick={() => onApply(draft)}>
          {text.apply}
        </Button>
        <Button
          size="sm"
          variant="primary"
          appearance="soft"
          disabled={onRolling}
          onClick={() => {
            setDraft({ from: rolling.from, to: rolling.to });
            onReset();
          }}
        >
          {text.last12}
        </Button>
      </div>
      <p className="month-range__applied" role="status">
        {loading ? text.updating : text.applied(formatMonth(range.from, labels), formatMonth(range.to, labels), range.from === range.to)}
      </p>
    </section>
  );
}
